import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export function canUseDevMockAuth({
  isDev,
  supabaseUrl,
  isDisabled,
}: {
  isDev: boolean;
  supabaseUrl: string;
  isDisabled: boolean;
}): boolean {
  if (!isDev || isDisabled) return false;
  return supabaseUrl.includes('127.0.0.1') || supabaseUrl.includes('localhost');
}

export function isDevMockAuth(): boolean {
  // Mock auth is a BROWSER-dev-only concept (auto-signs in test@example.com
  // against the local Supabase Docker stack). It must be a no-op in any Node
  // runtime — server routes, and especially Workflow DevKit steps, where the
  // data services (PostService/CampaignService → BaseService.execute) call
  // this. There, both `import.meta.env` and `localStorage` are undefined;
  // reading `.DEV` threw "Cannot read properties of undefined (reading 'DEV')"
  // and crashed every workflow step that touched a service (e.g.
  // generate-all-plan saveResult/finalizePlan → BUG-33).
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
    return canUseDevMockAuth({
      isDev: Boolean(env?.DEV),
      supabaseUrl: (env?.VITE_SUPABASE_URL as string) || '',
      isDisabled: localStorage.getItem('DISABLE_MOCK_AUTH') === 'true',
    });
  } catch {
    return false;
  }
}

/**
 * Check if there's an active session
 * Enhanced with better debugging and cleanup
 */
export function hasActiveSession(): boolean {
  try {
    // Extract the project ref from the URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const envProjectRef = import.meta.env.VITE_SUPABASE_PROJECT_REF;
    let projectRef: string = envProjectRef || '';
    let sessionData: string | null = null;
    let storageKey: string = '';

    if (supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1')) {
      // For local development, try multiple possible project refs
      const possibleRefs = ['127', 'localhost', 'local', 'supabase_local', '127.0.0.1'];

      for (const ref of possibleRefs) {
        const testKey = `sb-${ref}-auth-token`;
        sessionData = localStorage.getItem(testKey);
        if (sessionData) {
          projectRef = ref;
          storageKey = testKey;
          break;
        }
      }

      if (!sessionData) {
        // Fallback: check all localStorage keys for Supabase auth tokens
        const allKeys = Object.keys(localStorage);
        for (const key of allKeys) {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            sessionData = localStorage.getItem(key);
            if (sessionData) {
              storageKey = key;
              // Extract project ref from the key
              const match = key.match(/sb-(.+)-auth-token/);
              projectRef = match ? match[1] : 'unknown';
              break;
            }
          }
        }
      }

      if (!sessionData) {
        return false;
      }
    } else {
      // For production/staging, extract project ref from URL
      // Format: https://[project-ref].supabase.co
      const match = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/);
      projectRef = match ? match[1] : '';

      if (!projectRef) {
        logger.warn('hasActiveSession: Could not determine project ref from URL:', supabaseUrl);
        // Fallback for production: check all localStorage keys for Supabase auth tokens
        const allKeys = Object.keys(localStorage);
        for (const key of allKeys) {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            sessionData = localStorage.getItem(key);
            if (sessionData) {
              storageKey = key;
              const match = key.match(/sb-(.+)-auth-token/);
              projectRef = match ? match[1] : 'unknown';
              break;
            }
          }
        }
        if (!sessionData) return false;
      } else {
        storageKey = `sb-${projectRef}-auth-token`;
        sessionData = localStorage.getItem(storageKey);
      }

      if (!sessionData) {
        // One last fallback: try to find ANY token if the specific one wasn't found
        const allKeys = Object.keys(localStorage);
        for (const key of allKeys) {
          if (key.includes('sb-') && key.includes('-auth-token')) {
            sessionData = localStorage.getItem(key);
            if (sessionData) {
              storageKey = key;
              break;
            }
          }
        }

        if (!sessionData) return false;
      }
    }

    // At this point, sessionData should be available
    if (!sessionData) {
      return false;
    }

    const session = JSON.parse(sessionData);

    // Validate session structure
    if (!session?.access_token || !session?.expires_at) {
      logger.warn('hasActiveSession: Invalid session structure, cleaning up');
      localStorage.removeItem(storageKey);
      return false;
    }

    // Check expiration
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    const isExpired = expiresAt <= now;

    if (isExpired) {
      logger.warn(
        'hasActiveSession: Session token expired. Preserving storage for SDK auto-refresh.',
      );
      return false;
    }

    return true;
  } catch (error) {
    logger.error('hasActiveSession: Error checking session:', error);
    return false;
  }
}

/**
 * Get the current session asynchronously
 * This will make an API call if needed
 */
export async function getSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error || !session) {
    return null;
  }
  return session;
}
